import React, { useCallback, useEffect, useState } from 'react';
import IntlMessages from 'helpers/IntlMessages';
import { Formik, FormikValues } from 'formik';
import { Button, Form, FormGroup, CardImg } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { OnboardingProps } from './Onboarding';
import * as Yup from 'yup';
import CustomSpinner from 'components/common/CustomSpinner';
import { gql, useMutation } from '@apollo/client';
import CustomReactSelect, { Option } from 'components/custom/CustomReactSelect';
import useCommonsOptions from 'hooks/useCommonsOptions';
import { useEcoregionOptions } from 'hooks';

const COMPLETE_USER_ONBOARDING = gql`
  mutation CompleteUserOnboarding(
    $id: UUID!
    $name: String!
    $currentTerritoryId: UUID
    $bioregionGid: Int
  ) {
    updateUser(
      input: {
        id: $id
        patch: {
          name: $name
          currentTerritoryId: $currentTerritoryId
          bioregionGid: $bioregionGid
        }
      }
    ) {
      user {
        id
      }
    }
  }
`;

const WelcomeOnBoard = ({
  userInfo,
  onComplete,
}: Pick<OnboardingProps, 'userInfo'> & { onComplete: () => void }): React.ReactElement => {
  const intl = useIntl();
  const [clickedSubmit, setClickedSubmit] = useState(false);
  const [selectedCommons, setSelectedCommons] = useState<Option | null>();
  const [selectedEcoregion, setSelectedEcoregion] = useState<Option | null>();
  const { commonsLoading, commonsOptions } = useCommonsOptions();
  const { ecoregionOptions, ecoregionOptionsLoading } = useEcoregionOptions(false);

  /* */
  /* Queries and mutations */
  /* */
  const [completeUserOnboarding] = useMutation(COMPLETE_USER_ONBOARDING);

  useEffect(() => {
    if (
      !commonsLoading
      && selectedCommons === undefined
      && userInfo.currentUser.currentTerritoryId
    ) {
      const matchingCommons = commonsOptions?.find(
        (option: Option) => option.value === userInfo.currentUser.currentTerritoryId
      );
      if (matchingCommons) {
        setSelectedCommons(matchingCommons);
      }
    }
  }, [commonsLoading, commonsOptions, selectedCommons, userInfo.currentUser.currentTerritoryId]);

  useEffect(() => {
    if (
      !ecoregionOptionsLoading
      && selectedEcoregion === undefined
      && userInfo.currentUser.bioregionGid
    ) {
      const matchingEcoregion = ecoregionOptions.find(
        (option: Option) => Number(option.value) === userInfo.currentUser.bioregionGid
      );
      if (matchingEcoregion) {
        setSelectedEcoregion(matchingEcoregion);
      }
    }
  }, [
    ecoregionOptions,
    ecoregionOptionsLoading,
    selectedEcoregion,
    userInfo.currentUser.bioregionGid,
  ]);

  /* validation schema */
  const validationSchema = Yup.object().shape(
    {
      name: Yup.string().required(intl.formatMessage({ id: 'general.required' })).min(2, intl.formatMessage({ id: 'general.too-short' })),
    });

  const handleSubmitForm = useCallback((values: FormikValues) => {
    return completeUserOnboarding({
      variables: {
        id: userInfo.currentUser.id,
        name: values.name,
        currentTerritoryId:
          selectedCommons === undefined
            ? userInfo.currentUser.currentTerritoryId || null
            : selectedCommons?.value || null,
        bioregionGid:
          selectedEcoregion === undefined
            ? userInfo.currentUser.bioregionGid || null
            : selectedEcoregion?.value
              ? Number(selectedEcoregion.value)
              : null,
      },
    })
      .then(onComplete)
      .catch((error) => {
        console.error('Failed to complete user onboarding.', error);
      });
  }, [
    completeUserOnboarding,
    onComplete,
    selectedCommons,
    selectedEcoregion,
    userInfo.currentUser.bioregionGid,
    userInfo.currentUser.currentTerritoryId,
    userInfo.currentUser.id,
  ]);

  return (
    <div className="wizard-basic-step">
      <div className="d-flex justify-content-center mt-3 mb-1">
        <h1 className="mt-4 font-weight-bolder accent-color text-uppercase">
          <IntlMessages id="onboarding.welcome" />
        </h1>
      </div>
      <div className="d-flex justify-content-center">
        <CardImg
          src="/assets/img/welcome_on_board_logo.png"
          alt={intl.formatMessage({ id: 'onboarding.welcome-image' })}
          className="w-50 w-sm-100"
        />
      </div>
      <div className="mx-2 mx-xs-5 mt-4">
        <Formik
          initialValues={{ name: '', }}
          onSubmit={handleSubmitForm}
          validationSchema={validationSchema}
        >
          {({ errors, handleSubmit, isValid, dirty, handleBlur, handleChange, values, isSubmitting }) => (
            <Form
              className={`needs-validation ${dirty && isValid ? 'was-validated' : ''}`}
              onSubmit={handleSubmit}
              noValidate>
              <FormGroup style={{ position: 'relative' }}>
                <Form.Label className="font-weight-bolder" htmlFor="name">
                  <IntlMessages id="settings.name" />
                </Form.Label>
                <input
                  className={`form-control ${clickedSubmit && errors.name ? 'is-invalid' : ''}`}
                  name="name"
                  id="name"
                  type="text"
                  placeholder={intl.formatMessage({ id: 'onboarding.how-to-call-user' })}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.name}
                />
                {clickedSubmit && errors.name && (
                  <div className="invalid-feedback d-inline" >{errors.name}</div>
                )}
              </FormGroup>
              <FormGroup>
                <Form.Label className="font-weight-bolder">
                  <IntlMessages id="bioregion.commons" />
                </Form.Label>
                <CustomReactSelect
                  name="onboardingCommons"
                  options={commonsOptions}
                  selected={selectedCommons || undefined}
                  placeholder={intl.formatMessage({
                    id: 'general.select-or-type-to-search',
                  })}
                  isClearable
                  handleChange={(option: Option | null) => setSelectedCommons(option)}
                />
              </FormGroup>
              <FormGroup>
                <Form.Label className="font-weight-bolder">
                  <IntlMessages id="general.ecoregion" />
                </Form.Label>
                <CustomReactSelect
                  name="onboardingEcoregion"
                  options={ecoregionOptions}
                  selected={selectedEcoregion || undefined}
                  placeholder={intl.formatMessage({
                    id: 'general.select-or-type-to-search',
                  })}
                  isClearable
                  handleChange={(option: Option | null) => setSelectedEcoregion(option)}
                />
              </FormGroup>
              <div className="justify-content-center wizard-buttons mt-4">
                <Button
                  color="primary"
                  type='submit'
                  disabled={isSubmitting || (clickedSubmit && !isValid)}
                  onClick={() => { !isSubmitting && setClickedSubmit(true); }}>
                  {isSubmitting ?
                    <CustomSpinner />
                    :
                    <IntlMessages id="onboarding.start" />}
                </Button>
              </div>
            </Form>
          )}
        </Formik>

      </div>
    </div>
  );
};
export default WelcomeOnBoard;
